import { Control, Controller, FieldErrors, FieldValues, useFormContext, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Flex, FormField, TextInput, Select, Checkbox } from '@percent/lemonade';
import { Card } from '@percent/hosted-validation-form/ui';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';
import { HostedFormStep } from '../../HostedForm.types';
import { usePartnerConfig, useProgress } from '@percent/hosted-validation-form/hooks';

type RenderFieldProps = {
  type: 'short_text' | 'multiple_choice' | 'number' | 'select' | 'website';
  id: string;
  title: string;
  control: Control<FieldValues, object>;
  errors: FieldErrors<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  placeholder?: string;
  options?: {
    key: string;
    value: string;
  }[];
};

const RenderField = ({ type, id, title, control, errors, setValue, placeholder, options }: RenderFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const partnerFieldErrors = (errors.partnerFields as any)?.[id]?.message;

  if (type === 'multiple_choice') {
    return (
      <Controller
        name={`partnerFields.${id}`}
        control={control}
        render={({ field: { value } }) => (
          <FormField
            label={title}
            status={partnerFieldErrors ? 'danger' : 'default'}
            statusMessage={partnerFieldErrors}
          >
            <Flex direction="column" gap={16} w="100%">
              {options?.map(({ key, value: optionValue }) => (
                <Checkbox
                  key={key}
                  variant="default"
                  active={value?.includes(optionValue)}
                  name={key}
                  label={key}
                  onChange={(event) => {
                    const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked;
                    const currentValue = value;
                    let newArray = [];

                    if (currentValue && isChecked && Array.isArray(currentValue)) {
                      newArray = [...currentValue, optionValue];
                    } else if (currentValue && !isChecked && Array.isArray(currentValue)) {
                      newArray = [...currentValue].filter((el) => el !== optionValue);
                    } else {
                      newArray = [optionValue];
                    }
                    setValue(`partnerFields.${id}`, newArray, {
                      shouldValidate: true,
                    });
                  }}
                />
              ))}
            </Flex>
          </FormField>
        )}
      />
    );
  } else if (type === 'select') {
    const selectOptions = options?.map((option) => ({ value: option.value, label: option.key })) ?? [];

    return (
      <Controller
        name={`partnerFields.${id}`}
        control={control}
        defaultValue=""
        render={({ field: { value } }) => (
          <FormField
            label={title}
            status={partnerFieldErrors ? 'danger' : 'default'}
            statusMessage={partnerFieldErrors}
          >
            <Select
              options={selectOptions}
              defaultValue={selectOptions.find((element) => element.value === value)}
              placeholder={placeholder ?? ''}
              searchable
              onChange={(event) =>
                setValue(`partnerFields.${id}`, event.value, {
                  shouldValidate: true,
                })
              }
            />
          </FormField>
        )}
      />
    );
  }

  return (
    <Controller
      name={`partnerFields.${id}`}
      control={control}
      render={({ field }) => (
        <FormField label={title} status={partnerFieldErrors ? 'danger' : 'default'} statusMessage={partnerFieldErrors}>
          <TextInput
            value={field.value || ''}
            type={type === 'number' ? 'number' : undefined}
            name={id}
            onChange={(event) => {
              if (type === 'number') {
                const inputValue = event.target.value;
                setValue(`partnerFields.${id}`, inputValue === '' ? undefined : +inputValue, {
                  shouldValidate: true,
                });
              } else {
                setValue(`partnerFields.${id}`, event.target.value, {
                  shouldValidate: true,
                });
              }
            }}
            placeholder={placeholder ?? ''}
          />
        </FormField>
      )}
    />
  );
};

export const PartnerFields = () => {
  const { handleNext, handleBack } = useValidationStepData();
  const { control, getValues, setValue, formState } = useFormContext();
  const { partnerConfig } = usePartnerConfig();
  const { t } = useTranslation();
  const { progress } = useProgress();

  const handleNavigateNext = () => {
    handleNext(HostedFormStep.TERMS);
  };

  const checkDisabled = () => {
    let isDisabled = false;
    if (Object.keys(formState.errors).length > 0) {
      isDisabled = true;
    }
    partnerConfig?.configuration.partnerFields.map((field) => {
      const currentValue = getValues(`partnerFields.${field.id}`);
      if (field.validation?.required && !currentValue) {
        isDisabled = true;
      }

      if (
        field.validation?.required &&
        field.type === 'multiple_choice' &&
        Array.isArray(currentValue) &&
        currentValue.length === 0
      ) {
        isDisabled = true;
      }
    });
    return isDisabled;
  };

  return (
    <Card
      onBack={handleBack}
      title={t('hostedValidation.title.almostThere')}
      onNext={handleNavigateNext}
      nextButtonDisabled={checkDisabled()}
      nextButtonLabel={t('hostedValidation.button.next')}
      progress={progress}
    >
      <Flex direction="column" gap={16} w="100%">
        {partnerConfig?.configuration.partnerFields.map(({ id, type, title, placeholder, options }) => (
          <RenderField
            key={id}
            type={type}
            id={id}
            title={title}
            placeholder={placeholder}
            options={options}
            control={control}
            errors={formState.errors}
            setValue={setValue}
          />
        ))}
      </Flex>
    </Card>
  );
};
